import type { DefaultTheme } from 'styled-components';
import styled, { css } from 'styled-components';
import { Swiper } from 'swiper/react';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import { NextImageWithLoader } from '@/components/NextImageWithLoader';
import { Typography } from '@/components/Typography';
import { px2rem } from '@/theme';

import { ReactComponent as CloseSvg } from '@/assets/icons/close-icon.svg';
import { ReactComponent as Heart } from '@/assets/icons/heart-stroke.svg';
import { ReactComponent as Pin } from '@/assets/icons/Pin.svg';
import { ReactComponent as Pined } from '@/assets/icons/Pined.svg';
import { ReactComponent as Share } from '@/assets/icons/share.svg';

export const ProductLinkArea = styled(Flex)<{
  $backgroundColor?: string;
  css?: React.CSSProperties;
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  text-decoration: none;
  height: 100%;
  gap: ${px2rem(2)};
`;
export const CtaPlaceholder = styled(LegacyButton)`
  visibility: hidden;
  ${({ theme }) => css`
    ${theme.media.md.down} {
      margin-bottom: ${theme.spacing(0.6)};
    }
  `}
`;
export const Wrapper = styled(Flex)<{
  $backgroundColor?: string;
  $ctaType: 'hovered' | 'bottom';
  css?: React.CSSProperties;
}>`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  text-decoration: none;
  gap: ${px2rem(2)};
  overflow: hidden;

  ${({ $backgroundColor, $ctaType, theme }) => {
    const isHovered = $ctaType === 'hovered';

    return css`
      ${$backgroundColor &&
      css`
        background-color: ${$backgroundColor};
      `}

      padding-bottom: ${theme.spacing(2)};

      button {
        top: ${isHovered ? '40%' : 0};
        margin-top: ${isHovered ? 0 : theme.spacing(2)};
        opacity: ${isHovered ? 0 : 1};
        position: ${isHovered ? 'absolute' : 'relative'};
        transform: ${isHovered ? 'translate(-50%, -40%);' : 'none'};
        max-width: 90%;
        white-space: nowrap;
        font-size: 0.75rem;
        padding-inline: 10px;
        line-height: 15px;
        ${({ theme }) => css`
          ${theme.media.md.up} {
            max-width: 154px;
            line-height: inherit;
            white-space: inherit;
            padding: inherit;
          }
        `}
      }

      ${theme.media.md.up} {
        padding-bottom: ${theme.spacing(3)};
        & > img {
          transition: ${theme.transitions.create('opacity')};
        }

        ${isHovered &&
        css`
          button:disabled {
            opacity: 0;
          }
          &:hover {
            img {
              opacity: 0.3;
              transition: ${theme.transitions.create('opacity')};
            }
          }
        `}

        &:hover {
          button {
            opacity: 1;
          }

          button:disabled {
            opacity: 0.3;
          }
        }
      }
    `;
  }};
`;

export const ImageEmptyWrapper = styled(Flex)<{ withBackground: boolean }>`
  width: 100%;
  flex: 1 1 0%;
  align-items: center;
  overflow: hidden;

  ${({ theme, withBackground }) => css`
    background: ${withBackground ? theme.colors.white : 'transparent'};
    padding: ${withBackground ? '25px 0' : 0};

    ${theme.media.md.up} {
      background: transparent;
      padding: 0;
    }
  `}
`;

interface BackgroundStylesProps {
  $noPadding?: boolean;
  $withBackground: boolean;
  theme: DefaultTheme;
}
const backgroundStyles = ({
  $noPadding,
  $withBackground,
  theme,
}: BackgroundStylesProps) => css`
  background: ${$withBackground ? theme.colors.white : 'transparent'};
  padding: ${$withBackground && !$noPadding ? '25px 0' : 0};

  ${theme.media.md.up} {
    background: transparent;
    padding: 0;
  }
`;

const carouselImageStyles = (theme: DefaultTheme) => css`
  height: 95vw;
  width: 100%;
  object-fit: cover;
  @media (min-width: ${theme.breakpoints.md}) {
    height: 640px;
    max-height: 640px;
  }

  @media (min-width: 1200px) {
    height: 690px;
    max-height: 690px;
  }
`;

const cmsProductCardStyles = css`
  .next-image {
    height: 100%;
    width: auto;
  }
`;

const squareStrapStyles = css`
  margin-top: 18px;
`;

const watchSoldierImageStyles = (theme: DefaultTheme) => css`
  @media (max-width: 991px) {
    height: 250px;
    min-height: 250px;
    min-width: 250px;
  }
  @media (min-width: ${theme.breakpoints.md}) {
    height: 300px;
    min-height: 300px;
    min-width: 300px;
  }
  @media (min-width: 1280px) {
    height: 325px;
    min-height: 325px;
    min-width: 325px;
  }
  @media (min-width: 1440px) {
    height: 350px;
    min-height: 350px;
    min-width: 350px;
  }
`;

const squareCardStyles = css`
  @media (max-width: 767px) {
    width: auto;
    height: 270px;
    min-width: 270px;
  }

  @media (min-width: 768px) {
    height: auto;
    height: 360px;
    max-height: 360px;
    max-width: 360px;
    margin-top: 40px;
  }
`;

const twoHerosStyles = (theme: DefaultTheme) => css`
  @media (max-width: ${theme.breakpoints.md}) {
    height: 250px;
    min-width: 250px;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    min-height: 335px;
    max-height: 335px;
    min-width: 335px;
    margin-top: 40px;
  }
`;

const sharedImageStyles = css`
  .next-image {
    @media (max-width: 767px) {
      height: auto;
      width: 100%;
    }
    @media (min-width: 768px) {
      width: auto;
      height: 100%;
    }
  }
`;

interface NextImageStylesProps {
  $scale?: number;
  $scaleRatio?: number;
  $zoom?: number;
}

const nextImageStyles = ({
  $scale,
  $scaleRatio,
  $zoom,
}: NextImageStylesProps) => css`
  height: 100%;
  width: auto;

  ${$scaleRatio &&
  css`
    transform: scale(${$scaleRatio}) translateY(-${$scaleRatio * 2}%);
  `}
  ${$scale && `transform: scale(${$scale})`}

  ${$zoom &&
  css`
    transition: all 0.2s linear;
    &:hover {
      opacity: 1;
      transform: scale(${$zoom}) translateY(-${$zoom * 2}%);
    }
  `}
`;

export const Img = styled(NextImageWithLoader)<{
  $noPadding?: boolean;
  $scale?: number;
  $scaleRatio?: number;
  $withBackground: boolean;
  $zoom?: number;
}>`
  &&& {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    aspect-ratio: 1;
    margin-top: 4vw;
    ${({ theme }) => theme.media.sm.up} {
      margin-top: unset;
    }
    transition: all 0.7s ease-in-out;

    ${({ $noPadding, $withBackground, theme }) =>
      backgroundStyles({ $withBackground, theme, $noPadding })}

    &.carousel-image {
      ${({ theme }) => carouselImageStyles(theme)}
    }

    &.cms-product-card {
      ${cmsProductCardStyles}
    }

    &.square-strap {
      ${squareStrapStyles}
    }

    &.square-eyewear {
      ${squareStrapStyles}
    }

    &.watch-soldier-image {
      ${({ theme }) => watchSoldierImageStyles(theme)}
    }

    &.square-card {
      ${squareCardStyles}
    }

    &.two-heros {
      ${({ theme }) => twoHerosStyles(theme)}
    }

    &.two-heros,
    &.square-card {
      ${sharedImageStyles}
    }

    &.eyewear-card,
    &.strap-card,
    &.watch-card {
      object-fit: contain;
    }

    .next-image {
      ${({ $scale, $scaleRatio, $zoom }) =>
        nextImageStyles({ $scaleRatio, $scale, $zoom })}
    }
  }
`;

export const Details = styled(Flex)<{ gap?: number }>`
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  text-align: center;
  min-height: 90px;
  width: 100%;

  ${({ gap, theme }) => css`
    margin-top: ${theme.spacing(1)};

    ${gap &&
    css`
      gap: ${gap}px;
    `}
  `}
`;
export const CloseIcon = styled(CloseSvg)`
  width: 24px;
  height: 24px;
  position: absolute;
  top: 3%;
  right: 5%;
  cursor: pointer;
  :hover path {
    stroke: ${({ theme }) => theme.colors.primary};
  }
`;

export const PinIcon = styled(Pin)`
  width: 24px;
  height: 24px;
  position: absolute;
  top: 3%;
  left: 3%;
  cursor: pointer;
`;

export const PinedIcon = styled(Pined)`
  width: 24px;
  height: 24px;
  position: absolute;
  top: 3%;
  left: 3%;
  fill: ${({ theme }) => theme.colors.primary};
`;

export const ShareIcon = styled(Share)<{ positionRight?: boolean }>`
  width: 24px;
  height: 26px;
  position: absolute;
  top: 3%;
  ${({ positionRight }) => (positionRight ? `right: 0;` : `left: 7%;`)}

  &:hover circle,
  &:hover path {
    stroke: ${({ theme }) => theme.colors.primary};
  }
`;

export const FavIconWrapper = styled.button`
  && {
    margin-top: 0;
    background-color: transparent;
    position: absolute;
    border: none;
    width: 24px;
    ${({ theme }) => css`
      top: ${theme.spacing(0)};
      right: ${theme.spacing(2.5)};

      ${theme.media.md.up} {
        top: ${theme.spacing(1.5)};
        right: ${theme.spacing(1.5)};
      }
    `}
    z-index:1;
  }
`;

export const FavIcon = styled(Heart)<{
  $isFavIconWhite?: boolean;
  $isHovered?: boolean;
}>`
  width: 24px;
  path {
    ${({ $isFavIconWhite, $isHovered, theme }) =>
      $isFavIconWhite && $isHovered && `fill: ${theme.colors.white}`}
  }
  :hover path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;
export const FavIconed = styled(Icon)`
  width: 24px;
  path {
    fill: ${({ theme }) => theme.colors.primary} !important;
  }
`;
export const LabelWrapper = styled(Box)`
  position: absolute;
  max-width: 38%;

  ${({ theme }) => css`
    top: ${theme.spacing(1)};
    left: ${theme.spacing(1)};

    ${theme.media.md.up} {
      top: ${theme.spacing(1.5)};
      left: ${theme.spacing(1.5)};
    }
  `}
`;

export const LugSize = styled(Typography)`
  z-index: 1;
  position: absolute;

  ${({ theme }) => css`
    top: ${theme.spacing(1)};
    left: ${theme.spacing(1)};
    font-family: ${theme.fontFamilies.italianPlate};
    color: ${theme.colors.secondaryGrey};
    font-size: ${theme.fontSizes.p2};
  `}
`;

export const StrapNotCompare = styled(Typography)`
  ${({ theme }) => css`
    font-family: ${theme.fontFamilies.openSans};
    color: ${theme.colors.secondaryGrey};
    font-size: ${theme.fontSizes.p5};
  `}
`;

export const StyledSwiper = styled(Swiper)`
  width: 100%;
  flex: 1 1 auto;
  .swiper-button-prev,
  .swiper-button-next {
    color: #000;
    display: none;
    &::after {
      font-size: 16px;
    }
  }
  &:hover {
    .swiper-button-prev,
    .swiper-button-next {
      ${({ theme }) => css`
        ${theme.media.md.up} {
          display: flex;
        }
      `}
    }
    .swiper-pagination {
      visibility: visible;
    }
  }
  .swiper-pagination {
    height: 20px;
    width: 100%;
    bottom: 0;
    ${({ theme }) => css`
      ${theme.media.md.up} {
        visibility: hidden;
      }
    `}
    .swiper-pagination-bullet {
      width: 24px;
      height: 2px;
      border-radius: 0;
    }
    .swiper-pagination-bullet-active {
      background-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const CardZone = styled(Flex)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const heightForTwoLineLabels = '73px';

export const CardZoneSkuLabelPriceGroup = styled(Flex)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  width: 100%;
  min-height: ${heightForTwoLineLabels};
`;
